import { __assign, __read, __spread, __values } from "tslib";
import { UPlotScaleBuilder } from './UPlotScaleBuilder';
import { UPlotSeriesBuilder } from './UPlotSeriesBuilder';
import { UPlotAxisBuilder } from './UPlotAxisBuilder';
import { AxisPlacement } from '../config';
import uPlot from 'uplot';
import { defaultsDeep } from 'lodash';
import { DefaultTimeZone, getTimeZoneInfo } from '@grafana/data';
var UPlotConfigBuilder = /** @class */ (function () {
    function UPlotConfigBuilder(getTimeZone) {
        var _this = this;
        if (getTimeZone === void 0) { getTimeZone = function () { return DefaultTimeZone; }; }
        this.getTimeZone = getTimeZone;
        this.series = [];
        this.axes = {};
        this.scales = [];
        this.bands = [];
        this.hasLeftAxis = false;
        this.hasBottomAxis = false;
        this.hooks = {};
        this.tzDate = function (ts) {
            var _a;
            if (!_this.getTimeZone) {
                return new Date(ts);
            }
            var tz = (_a = getTimeZoneInfo(_this.getTimeZone(), Date.now())) === null || _a === void 0 ? void 0 : _a.ianaName;
            if (!tz) {
                return new Date(ts);
            }
            return uPlot.tzDate(new Date(ts), tz);
        };
    }
    UPlotConfigBuilder.prototype.addHook = function (type, hook) {
        if (!this.hooks[type]) {
            this.hooks[type] = [];
        }
        this.hooks[type].push(hook);
    };
    UPlotConfigBuilder.prototype.addAxis = function (props) {
        var _a;
        props.placement = (_a = props.placement) !== null && _a !== void 0 ? _a : AxisPlacement.Auto;
        if (this.axes[props.scaleKey]) {
            this.axes[props.scaleKey].merge(props);
            return;
        }
        // Handle auto placement logic
        if (props.placement === AxisPlacement.Auto) {
            props.placement = this.hasLeftAxis ? AxisPlacement.Right : AxisPlacement.Left;
        }
        switch (props.placement) {
            case AxisPlacement.Left:
                this.hasLeftAxis = true;
                break;
            case AxisPlacement.Bottom:
                this.hasBottomAxis = true;
                break;
        }
        if (props.placement === AxisPlacement.Hidden) {
            props.show = false;
            props.size = 0;
        }
        this.axes[props.scaleKey] = new UPlotAxisBuilder(props);
    };
    UPlotConfigBuilder.prototype.getAxisPlacement = function (scaleKey) {
        var _a;
        var axis = this.axes[scaleKey];
        return (_a = axis === null || axis === void 0 ? void 0 : axis.props.placement) !== null && _a !== void 0 ? _a : AxisPlacement.Left;
    };
    UPlotConfigBuilder.prototype.setCursor = function (cursor) {
        this.cursor = cursor;
    };
    // uPlot types don't export the Select interface prior to 1.6.4
    UPlotConfigBuilder.prototype.setSelect = function (select) {
        this.select = select;
    };
    UPlotConfigBuilder.prototype.addSeries = function (props) {
        this.series.push(new UPlotSeriesBuilder(props));
    };
    UPlotConfigBuilder.prototype.getSeries = function () {
        return this.series;
    };
    /** Add or update the scale with the scale key */
    UPlotConfigBuilder.prototype.addScale = function (props) {
        var current = this.scales.find(function (v) { return v.props.scaleKey === props.scaleKey; });
        if (current) {
            current.merge(props);
            return;
        }
        this.scales.push(new UPlotScaleBuilder(props));
    };
    UPlotConfigBuilder.prototype.addBand = function (band) {
        this.bands.push(band);
    };
    UPlotConfigBuilder.prototype.getConfig = function () {
        var e_1, _a;
        var _b;
        var config = { series: [{}] };
        config.axes = this.ensureNonOverlappingAxes(Object.values(this.axes)).map(function (a) { return a.getConfig(); });
        config.series = __spread(config.series, this.series.map(function (s) { return s.getConfig(); }));
        config.scales = this.scales.reduce(function (acc, s) {
            return __assign(__assign({}, acc), s.getConfig());
        }, {});
        config.hooks = this.hooks;
        /* @ts-ignore */
        // uPlot types don't export the Select interface prior to 1.6.4
        config.select = this.select;
        config.cursor = this.cursor || {};
        config.tzDate = this.tzDate;
        // When bands exist, only keep fill when defined
        if ((_b = this.bands) === null || _b === void 0 ? void 0 : _b.length) {
            config.bands = this.bands;
            var keepFill = new Set();
            try {
                for (var _c = __values(config.bands), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var b = _d.value;
                    keepFill.add(b.series[0]);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                }
                finally { if (e_1) throw e_1.error; }
            }
            for (var i = 1; i < config.series.length; i++) {
                if (!keepFill.has(i)) {
                    config.series[i].fill = undefined;
                }
            }
        }
        var cursorDefaults = {
            // prevent client-side zoom from triggering at the end of a selection
            drag: { setScale: false },
            points: {
                /*@ts-ignore*/
                size: function (u, seriesIdx) { return u.series[seriesIdx].points.size * 2; },
                /*@ts-ignore*/
                width: function (u, seriesIdx, size) { return size / 4; },
                /*@ts-ignore*/
                stroke: function (u, seriesIdx) { return u.series[seriesIdx].points.stroke(u, seriesIdx) + '80'; },
                /*@ts-ignore*/
                fill: function (u, seriesIdx) { return u.series[seriesIdx].points.stroke(u, seriesIdx); },
            },
            focus: {
                prox: 30,
            },
        };
        defaultsDeep(config.cursor, cursorDefaults);
        return config;
    };
    UPlotConfigBuilder.prototype.ensureNonOverlappingAxes = function (axes) {
        var e_2, _a;
        try {
            for (var axes_1 = __values(axes), axes_1_1 = axes_1.next(); !axes_1_1.done; axes_1_1 = axes_1.next()) {
                var axis = axes_1_1.value;
                if (axis.props.placement === AxisPlacement.Right && this.hasLeftAxis) {
                    axis.props.grid = false;
                }
                if (axis.props.placement === AxisPlacement.Top && this.hasBottomAxis) {
                    axis.props.grid = false;
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (axes_1_1 && !axes_1_1.done && (_a = axes_1.return)) _a.call(axes_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        return axes;
    };
    return UPlotConfigBuilder;
}());
export { UPlotConfigBuilder };
//# sourceMappingURL=UPlotConfigBuilder.js.map